
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ComparisonState, Orientation, Position, Size } from '../types';

interface ComparisonCanvasProps {
  state: ComparisonState;
  onSplitChange: (pos: number) => void;
  onLabelMove: (target: 'before' | 'after', pos: Position) => void;
  onLabelResize: (target: 'before' | 'after', size: Size) => void;
  exportRef: React.RefObject<HTMLDivElement | null>;
}

export const ComparisonCanvas: React.FC<ComparisonCanvasProps> = ({
  state,
  onSplitChange,
  onLabelMove,
  onLabelResize,
  exportRef,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const [draggingLabel, setDraggingLabel] = useState<'before' | 'after' | null>(null);
  const [resizingLabel, setResizingLabel] = useState<'before' | 'after' | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number, y: number, w: number, h: number } | null>(null);

  const {
    beforeImage,
    afterImage,
    title,
    beforeLabel,
    afterLabel,
    beforeDesc,
    afterDesc,
    orientation,
    viewMode,
    splitPosition,
    separatorColor,
    separatorWidth,
    beforeLabelPos,
    afterLabelPos,
    beforeLabelSize,
    afterLabelSize,
    objectFit,
    fontFamily,
    fontSize,
    accentColor,
    bgColor,
    theme,
    borderRadius,
    padding,
    labelColor,
    descColor
  } = state;

  const activeObjectFit = viewMode === 'slider' ? 'cover' : objectFit;

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    if (isDraggingSlider && viewMode === 'slider') {
      let newPosition: number;
      if (orientation === 'horizontal') {
        newPosition = ((clientX - rect.left) / rect.width) * 100;
      } else {
        newPosition = ((clientY - rect.top) / rect.height) * 100;
      }
      onSplitChange(Math.max(0, Math.min(100, newPosition)));
    }

    if (draggingLabel) {
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      onLabelMove(draggingLabel, { x, y });
    }

    if (resizingLabel && resizeStart) {
      const deltaX = clientX - resizeStart.x;
      const deltaY = clientY - resizeStart.y;
      onLabelResize(resizingLabel, {
        width: Math.max(100, resizeStart.w + deltaX),
        height: Math.max(40, resizeStart.h + deltaY)
      });
    }
  }, [isDraggingSlider, draggingLabel, resizingLabel, resizeStart, orientation, viewMode, onSplitChange, onLabelMove, onLabelResize]);

  useEffect(() => {
    const handleGlobalMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const handleGlobalTouch = (e: TouchEvent) => handleMove(e.touches[0].clientX, e.touches[0].clientY);
    const stopDragging = () => {
      setIsDraggingSlider(false);
      setDraggingLabel(null);
      setResizingLabel(null);
      setResizeStart(null);
    };

    window.addEventListener('mousemove', handleGlobalMove);
    window.addEventListener('touchmove', handleGlobalTouch);
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('touchend', stopDragging);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMove);
      window.removeEventListener('touchmove', handleGlobalTouch);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [handleMove]);

  const getThemeStyles = () => {
    switch(theme) {
      case 'neobrutalist':
        return {
          container: "border-[4px] border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]",
          label: "border-[3px] border-black shadow-[6px_6px_0px_rgba(0,0,0,1)] rounded-none bg-white",
          handle: "rounded-none border-[3px] border-black bg-white"
        };
      case 'cyberpunk':
        return {
          container: "border-2 border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.3)]",
          label: "border border-magenta-500 bg-black/80 backdrop-blur-md rounded-none skew-x-[-10deg]",
          handle: "rounded-none border-2 border-cyan-400 bg-black animate-pulse shadow-[0_0_10px_cyan]"
        };
      case 'material':
        return {
          container: "border-none shadow-2xl",
          label: "bg-white/90 backdrop-blur shadow-lg rounded-full",
          handle: "rounded-full shadow-lg bg-white"
        };
      case 'bauhaus':
        return {
          container: "border-[12px] border-slate-950",
          label: `rounded-none bg-white border-4 border-slate-950 font-bold`,
          handle: "rounded-none bg-[#FFD700] border-4 border-slate-950"
        };
      case 'bold':
        return {
          container: "border-[8px] border-white/10",
          label: "bg-black px-8 py-4 rounded-none uppercase italic border-2 border-white/20",
          handle: "rounded-full bg-white"
        };
      case 'flat':
        return {
          container: "border-none shadow-none",
          label: "rounded-none shadow-none",
          handle: "rounded-none bg-white shadow-none"
        };
      case 'glassmorphism':
        return {
          container: "border border-white/20 shadow-2xl",
          label: "bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl",
          handle: "rounded-full bg-white/30 backdrop-blur-md border border-white/20"
        };
      case 'minimal':
        return {
          container: "border border-slate-200 shadow-sm",
          label: "bg-white border border-slate-200 rounded-sm shadow-sm",
          handle: "rounded-full bg-slate-900 border-2 border-white"
        };
      case 'dark_mode_2':
        return {
          container: "border border-slate-800 shadow-2xl ring-1 ring-white/10",
          label: "bg-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-2xl",
          handle: "rounded-full bg-slate-100 border border-slate-300 shadow-xl"
        };
      default:
        return {
          container: "border border-slate-800 shadow-2xl",
          label: "rounded-2xl bg-slate-900/60 backdrop-blur-xl border border-white/20",
          handle: "rounded-full shadow-xl bg-white"
        };
    }
  };

  const themeClasses = getThemeStyles();

  const renderLabel = (labelTitle: string, desc: string, pos: Position, size: Size, type: 'before' | 'after') => (
    <div 
      className={`absolute z-40 cursor-grab active:cursor-grabbing transition-transform active:scale-95 flex flex-col group ${themeClasses.label}`}
      onMouseDown={(e) => { e.stopPropagation(); setDraggingLabel(type); }}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: 'translate(-50%, -50%)',
        fontFamily: fontFamily,
        width: `${size.width}px`,
        height: `${size.height}px`,
        padding: '1.25rem',
        overflow: 'hidden',
        borderRadius: (theme === 'bauhaus' || theme === 'neobrutalist' || theme === 'flat' || theme === 'bold') ? '0' : undefined
      }}
    >
      <h4 className="font-black uppercase tracking-widest mb-1 leading-tight flex-shrink-0" style={{ fontSize: `${fontSize}px`, color: labelColor }}>
        {labelTitle}
      </h4>
      {desc && <p className="text-[11px] opacity-90 leading-relaxed font-medium whitespace-pre-wrap overflow-y-auto custom-scrollbar" style={{ color: descColor }}>{desc}</p>}
      
      <div 
        className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-end justify-end p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        onMouseDown={(e) => {
          e.stopPropagation();
          setResizingLabel(type);
          setResizeStart({ x: e.clientX, y: e.clientY, w: size.width, h: size.height });
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-slate-400 rotate-90">
          <path d="M21 3L3 21M21 13L13 21" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );

  const radiusValue = `${borderRadius}px`;

  const getImageWrapperStyle = (type: 'before' | 'after'): React.CSSProperties => {
    const commonPadding = padding;
    const isHorizontal = orientation === 'horizontal';
    const basePadding = `${commonPadding}px`;

    if (viewMode === 'slider') {
      return {
        padding: basePadding,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box'
      };
    }

    const halfGap = separatorWidth / 2;
    return {
      padding: basePadding,
      paddingRight: isHorizontal && type === 'before' ? `${commonPadding + halfGap}px` : basePadding,
      paddingLeft: isHorizontal && type === 'after' ? `${commonPadding + halfGap}px` : basePadding,
      paddingBottom: !isHorizontal && type === 'before' ? `${commonPadding + halfGap}px` : basePadding,
      paddingTop: !isHorizontal && type === 'after' ? `${commonPadding + halfGap}px` : basePadding,
      width: '100%',
      height: '100%',
      boxSizing: 'border-box'
    };
  };

  const renderImage = (src: string, type: 'before' | 'after') => {
    const isContain = activeObjectFit === 'contain';
    return (
      <div style={getImageWrapperStyle(type)} className="w-full h-full">
        <div className="w-full h-full flex items-center justify-center overflow-hidden" style={{ borderRadius: radiusValue }}>
          <img 
            src={src} 
            alt={type} 
            style={{
              width: isContain ? 'auto' : '100%',
              height: isContain ? 'auto' : '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: activeObjectFit,
              borderRadius: radiusValue,
              display: 'block'
            }} 
          />
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={exportRef}
      className="relative flex flex-col items-center select-none w-full" 
      style={{ 
        backgroundColor: bgColor, 
        paddingTop: '5rem',
        paddingBottom: '5rem',
        paddingLeft: '5rem', 
        paddingRight: '5rem' 
      }}
    >
      {title && (
        <h2 
          className="mb-12 font-black text-center uppercase tracking-[0.2em] w-full px-4" 
          style={{ 
            fontFamily, 
            fontSize: `${fontSize * 1.5}px`, 
            color: accentColor,
            textShadow: theme === 'cyberpunk' ? `0 0 10px ${accentColor}` : undefined
          }}
        >
          {title}
        </h2>
      )}

      <div className="relative overflow-visible w-full max-w-[900px]">
        <div 
          ref={containerRef}
          className={`relative select-none overflow-hidden mx-auto ${themeClasses.container}`}
          style={{ 
            height: '700px', 
            width: '100%',
            backgroundColor: bgColor,
            borderRadius: radiusValue
          }}
        >
          {viewMode === 'slider' ? (
            <>
              <div className="absolute inset-0">
                 {renderImage(beforeImage!, 'before')}
              </div>
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ 
                  clipPath: orientation === 'horizontal' 
                    ? `inset(0 ${100 - splitPosition}% 0 0)` 
                    : `inset(0 0 ${100 - splitPosition}% 0)`
                }}
              >
                 {renderImage(afterImage!, 'after')}
              </div>
              <div 
                onMouseDown={(e) => { e.stopPropagation(); setIsDraggingSlider(true); }}
                className={`absolute z-30 flex items-center justify-center cursor-move`}
                style={{
                  left: orientation === 'horizontal' ? `${splitPosition}%` : '0',
                  top: orientation === 'vertical' ? `${splitPosition}%` : '0',
                  width: orientation === 'horizontal' ? `${separatorWidth}px` : '100%',
                  height: orientation === 'vertical' ? `${separatorWidth}px` : '100%',
                  backgroundColor: separatorColor,
                  transform: orientation === 'horizontal' ? 'translateX(-50%)' : 'translateY(-50%)',
                  boxShadow: theme === 'cyberpunk' ? `0 0 15px ${separatorColor}` : undefined
                }}
              >
                <div className={`w-12 h-12 flex items-center justify-center border-4 ${themeClasses.handle}`} style={{ borderColor: separatorColor }}>
                   <div className={`w-1.5 h-6 rounded-full ${orientation === 'vertical' ? 'rotate-90' : ''}`} style={{ backgroundColor: theme === 'bauhaus' ? '#000' : '#ddd' }} />
                </div>
              </div>
            </>
          ) : (
            <div className={`flex w-full h-full overflow-hidden ${orientation === 'vertical' ? 'flex-col' : 'flex-row'}`}>
              <div className="flex-1 overflow-hidden relative">
                {renderImage(beforeImage!, 'before')}
              </div>
              <div 
                style={{ 
                  width: orientation === 'horizontal' ? `${separatorWidth}px` : '100%',
                  height: orientation === 'vertical' ? `${separatorWidth}px` : '100%',
                  backgroundColor: separatorColor,
                }}
              />
              <div className="flex-1 overflow-hidden relative">
                {renderImage(afterImage!, 'after')}
              </div>
            </div>
          )}
        </div>
        {renderLabel(beforeLabel, beforeDesc, beforeLabelPos, beforeLabelSize, 'before')}
        {renderLabel(afterLabel, afterDesc, afterLabelPos, afterLabelSize, 'after')}
      </div>
    </div>
  );
};
